/**
 * ImpersonationBanner — persistent indicator shown while a TrainLoop staff
 * member is impersonating another team (read-only).
 *
 * Rendered globally by AppLayout. Hidden when not impersonating. The team
 * NAME is resolved from /api/internal/teams (a staff-only endpoint) because
 * /api/auth/me intentionally reports the staff member's OWN teams, not the
 * impersonated one.
 *
 * On a user-scoped Settings section (Teams, per the registry in
 * components/settings/sections.ts) the banner says the section shows the
 * staff member's own teams: that section reads /api/auth/me, which
 * impersonation never re-scopes, and claiming otherwise is what made the
 * old mixed-scope Settings page confusing.
 */

import { Eye, EyeOff, X } from 'lucide-react'
import { useLocation } from 'react-router-dom'

import { settingsScopeForPath } from '@/components/settings/sections'
import { Button } from '@/components/ui/button'
import { useTeam } from '@/contexts/TeamContext'
import { useInternalTeams } from '@/hooks/useInternalTeams'

export function ImpersonationBanner(): React.JSX.Element | null {
  const { impersonatingTeamId, isEmployee, stopImpersonation } = useTeam()
  const { pathname } = useLocation()

  // Only staff can impersonate; resolve the target team's display name.
  const teamsQuery = useInternalTeams({ enabled: isEmployee && !!impersonatingTeamId })

  if (!impersonatingTeamId) {
    return null
  }

  const team = teamsQuery.data?.teams.find(t => t.id === impersonatingTeamId)
  const label = team?.name ?? impersonatingTeamId
  const selfScoped = settingsScopeForPath(pathname) === 'user'

  return (
    <div
      role="status"
      className={
        selfScoped
          ? 'flex items-center justify-center gap-3 bg-muted px-4 py-2 text-sm font-medium text-muted-foreground'
          : 'flex items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-sm font-medium text-amber-950'
      }
    >
      {selfScoped ? (
        <>
          <EyeOff className="h-4 w-4" aria-hidden="true" />
          <span>
            Impersonating <strong>{label}</strong> (read-only). This section shows
            your own teams.
          </span>
        </>
      ) : (
        <>
          <Eye className="h-4 w-4" aria-hidden="true" />
          <span>
            Impersonating <strong>{label}</strong> (read-only)
          </span>
        </>
      )}
      <Button
        variant="ghost"
        size="sm"
        className={
          selfScoped
            ? 'h-7 gap-1 hover:bg-muted-foreground/10'
            : 'h-7 gap-1 text-amber-950 hover:bg-amber-600/40'
        }
        onClick={() => {
          stopImpersonation()
        }}
      >
        <X className="h-3.5 w-3.5" />
        Exit
      </Button>
    </div>
  )
}
